import { z } from 'zod';

/**
 * Zod schemas for the BP-03 `/clients/*` responses (ARCHITECTURE §3 Validation).
 *
 * **Masking is a shape, not a flag.** Rule 14: a caller holding `client.read` but not
 * `client.read_full` — a technician — gets `ClientMaskedOut`, which *has no*
 * `phone_e164`, `email` or `dob` property at all. So those three are optional here and
 * the UI reads `phone_masked`, which every caller gets. There is no branch in this app
 * that unmasks a number: the full value simply never arrives (BP-03 §12's schema test
 * asserts the same thing on the api side).
 *
 * Hand-written rather than derived from `src/lib/api/schema.d.ts` for the reason
 * `features/dpdp/schemas.ts` gives: that file is generated in CI from an OpenAPI document
 * that only contains a route once `app/main.py` mounts it.
 */

/** One entry of `client_nail_profile.allergies`. */
export const allergySchema = z.object({
  code: z.string(),
  label: z.string(),
  severity: z.enum(['mild', 'moderate', 'severe']),
  note: z.string().nullable(),
});

/** A client, masked or not (rule 14). */
export const clientSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone_masked: z.string().nullable(),
  gender: z.string(),
  notes: z.string().nullable(),
  tags: z.array(z.string()),
  source: z.string(),
  status: z.string(),
  merged_into: z.string().nullable(),
  erased_at: z.string().nullable(),
  first_visit_at: z.string().nullable(),
  preferred_locale: z.string(),
  live_consents: z.array(z.string()),
  created_at: z.string(),
  updated_at: z.string(),
  // Present only for a caller holding `client.read_full` (rule 14).
  phone_e164: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  dob: z.string().nullable().optional(),
  possible_duplicates: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        phone_masked: z.string().nullable(),
        similarity: z.number().nullable(),
      }),
    )
    .optional(),
});

export const clientPageSchema = z.object({
  items: z.array(clientSchema),
  next_cursor: z.string().nullable(),
});

export const nailProfileSchema = z.object({
  client_id: z.string(),
  shape: z.string().nullable(),
  length: z.string().nullable(),
  finish_pref: z.string().nullable(),
  allergies: z.array(allergySchema),
  sensitivities: z.string().nullable(),
  technician_pref: z.string().nullable(),
  dos: z.string().nullable(),
  donts: z.string().nullable(),
  updated_at: z.string(),
});

export const visitSummarySchema = z.object({
  visit_count: z.number(),
  last_visit_at: z.string().nullable(),
  first_visit_at: z.string().nullable(),
  lifetime_spend_paise: z.number(),
  avg_ticket_paise: z.number(),
  no_show_count: z.number(),
  last_services: z.array(z.object({ name: z.string(), at: z.string() })),
  last_shades: z.array(
    z.object({
      sku_id: z.string(),
      brand: z.string(),
      shade_name: z.string(),
      at: z.string(),
    }),
  ),
  last_technician: z.object({ staff_id: z.string(), name: z.string() }).nullable(),
  outstanding_paise: z.number(),
  source: z.string(),
});

/** One row of the consent ledger (rule 5); the evidence arrives already redacted. */
export const consentSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  purpose: z.string(),
  granted_at: z.string().nullable(),
  withdrawn_at: z.string().nullable(),
  channel: z.string(),
  notice_version: z.string(),
  superseded_by: z.string().nullable(),
  actor_id: z.string().nullable(),
  is_live: z.boolean(),
  needs_reconfirmation: z.boolean(),
  evidence: z.record(z.string(), z.unknown()),
  created_at: z.string(),
  updated_at: z.string(),
});

export const consentListSchema = z.array(consentSchema);

export const tagsSchema = z.object({ tags: z.array(z.string()) });

export const mergeSchema = clientSchema.extend({
  reassigned_counts: z.record(z.string(), z.number()),
  pending_modules: z.array(z.string()),
});

export type Allergy = z.infer<typeof allergySchema>;
export type Client = z.infer<typeof clientSchema>;
export type ClientPage = z.infer<typeof clientPageSchema>;
export type NailProfile = z.infer<typeof nailProfileSchema>;
export type VisitSummary = z.infer<typeof visitSummarySchema>;
export type Consent = z.infer<typeof consentSchema>;
export type MergeResult = z.infer<typeof mergeSchema>;

/**
 * The six grantable purposes, in the order §6's consent screen shows them.
 *
 * `ar_training` joined the list in BP-20; before that the api refused it with a 422,
 * so a toggle for it would have been a promise the server did not keep.
 */
export const GRANTABLE_PURPOSES = [
  'service_record',
  'reminders',
  'marketing',
  'photo_storage',
  'photo_marketing',
  'ar_training',
] as const;

export type GrantablePurpose = (typeof GRANTABLE_PURPOSES)[number];

/** Rule 4: `service_record` is required to create a client at all. */
export const REQUIRED_PURPOSE = 'service_record';

/** Rule 4: `photo_marketing` needs a live `photo_storage`; BP-20 rule 1a: so does
 * `ar_training`. */
export const PURPOSE_DEPENDENCIES: Readonly<Record<string, string>> = {
  photo_marketing: 'photo_storage',
  ar_training: 'photo_storage',
};

/** `client_nail_profile.shape` (BP-03 §3). */
export const NAIL_SHAPES = [
  'square',
  'squoval',
  'round',
  'oval',
  'almond',
  'coffin',
  'stiletto',
  'lipstick',
  'other',
] as const;

/** `client_nail_profile.length`. */
export const NAIL_LENGTHS = ['short', 'medium', 'long', 'extra_long'] as const;

/** `client.source`. */
export const CLIENT_SOURCES = ['walkin', 'web', 'app', 'staff', 'whatsapp', 'import'] as const;

/** `client.gender`. */
export const GENDERS = ['female', 'male', 'other', 'unspecified'] as const;
